import React, { useState, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../store";

import InventoryFilter from "./inventory/InventoryFilter";
import Inventory from "./inventory/Inventory";
import ToBeSoldItems from "./ToBeSoldItems";

import {
  SelectedGroupedItem,
  GroupedInventoryItem,
} from "./types/Inventory";

import { useIsDesktop } from "../../../hooks/useIsDesktop";

import {
  closeFilter,
  openFilter,
} from "../../../store/slices/sidebarFilterSlice";
import {moneyFromMinor} from '../../utils/UnitConversion.utils';
import {defaultFilterState, InventoryFilterState} from '../../skin-db/database/filter/SkinDbFilter';

export type SortOption =
    | "price_desc"
    | "price_asc"
    | "title_asc"
    | "title_desc"
    | "float_asc"
    | "float_desc";

const Sell: React.FC = () => {
  const dispatch = useDispatch();
  const onItemsSoldRef = useRef<((assetIds: string[]) => void) | null>(null);

  const steamAccountId = useSelector(
      (state: RootState) => state.auth.selectedSteamAccount?.id
  );

  const steamAccountSteamId = useSelector(
      (state: RootState) => state.auth.selectedSteamAccount?.steamId
  );

  const tradeable = useSelector(
      (state: RootState) => state.auth.selectedSteamAccount?.tradeable
  );

  const filterCollapsed = useSelector(
      (state: RootState) => state.sidebarFilter.collapsed
  );

  const isDesktop = useIsDesktop();

  const [filters, setFilters] = useState<InventoryFilterState>(defaultFilterState);
  const [selectedItems, setSelectedItems] = useState<SelectedGroupedItem[]>([]);
  const [sortBy, setSortBy] = useState<SortOption>("price_desc");
  const [searchTerm, setSearchTerm] = useState("");
  const [mobileCartOpen, setMobileCartOpen] = useState(false);

  useEffect(() => {
    if (isDesktop) {
      dispatch(openFilter());
    } else {
      dispatch(closeFilter());
    }
  }, [isDesktop, dispatch]);

  // RESET FUNCTION
  const handleResetAll = () => {
    setFilters({
      ...defaultFilterState,
      name: searchTerm
    });
  };

  const handleFilterChange = (next: InventoryFilterState) => setFilters(next);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setFilters((prev) => ({ ...prev, name: value }));
  };

  const handleSortChange = (value: SortOption) => setSortBy(value);

  const toggleSelectItem = (group: GroupedInventoryItem) => {
    setSelectedItems((prev) => {
      const exists = prev.some((i) => i.key === group.key);
      if (exists) return prev.filter((i) => i.key !== group.key);

      return [
        ...prev,
        {
          key: group.key,
          representative: group.representative,
          items: group.items,
          quantity: 1,
          maxQuantity: group.totalCount,
          price: group.representative.catalog?.priceRealMinor != null && group.representative.catalog.priceRealMinor > 0
              ? moneyFromMinor(group.representative.catalog.priceRealMinor)
              : 1,
        },
      ];
    });
  };

  const removeSelectedItem = (key: string) => {
    setSelectedItems((prev) => prev.filter((i) => i.key !== key));
  };

  const clearSelection = () => setSelectedItems([]);

  const updateQuantity = (key: string, quantity: number) => {
    setSelectedItems((prev) =>
        prev.map((item) => (item.key === key ? { ...item, quantity } : item))
    );
  };

  const updatePrice = (key: string, price: number) => {
    setSelectedItems((prev) =>
        prev.map((item) => (item.key === key ? { ...item, price } : item))
    );
  };

  const handleRemoveSoldItemsFromState = (soldAssetIds: string[]) => {
    if (onItemsSoldRef.current) {
      onItemsSoldRef.current(soldAssetIds);
    }
  };

  return (
      <Box className="md:p-4 p-2 mt-20 h-[92vh]">

        {!filterCollapsed && !isDesktop && (
            <div className="lg:hidden fixed left-0 right-0 bottom-0 top-[65px] z-50 bg-[#0d0e11] overflow-y-auto flex justify-center">
              <div className="w-full max-w-md">
                <InventoryFilter
                    filters={filters}
                    onChange={handleFilterChange}
                    onClose={() => dispatch(closeFilter())}
                    collapsed={false}
                />
              </div>
            </div>
        )}

        {selectedItems.length > 0 && (
            <button
                className="lg:hidden fixed bottom-16 mb-2 left-4 right-4 z-40 bg-blue-600 text-white py-3 rounded-lg font-semibold shadow-lg"
                onClick={() => setMobileCartOpen(true)}
            >
              Sell {selectedItems.reduce((s, i) => s + i.quantity, 0)} Items
            </button>
        )}

        {mobileCartOpen && (
            <div className="lg:hidden fixed left-0 right-0 bottom-0 top-[65px] z-50 bg-neutral-800 overflow-y-auto flex justify-center p-4">
              <div className="w-full max-w-md md:mt-10">
                <ToBeSoldItems
                    items={selectedItems}
                    steamAccountId={steamAccountId!}
                    onRemoveItem={removeSelectedItem}
                    onClear={clearSelection}
                    onQuantityChange={updateQuantity}
                    onPriceChange={updatePrice}
                    onCloseMobileCart={() => setMobileCartOpen(false)}
                    onSuccess={handleRemoveSoldItemsFromState}
                />
              </div>
            </div>
        )}

        <div className="flex gap-4 items-start">

          <div
              className={`hidden lg:block flex-shrink-0 overflow-hidden transition-all duration-300 ${
                  filterCollapsed ? "w-0 opacity-0" : "w-72 opacity-100"
              }`}
          >
            <InventoryFilter
                filters={filters}
                onChange={handleFilterChange}
                onClose={() => dispatch(closeFilter())}
                collapsed={false}
            />
          </div>

          <div className="flex-1">
            <Inventory
                steamAccountId={steamAccountId ?? undefined}
                steamAccountSteamId={steamAccountSteamId ?? undefined}
                tradeable={tradeable ?? undefined}
                filters={filters}
                selectedItems={selectedItems}
                onToggleSelectItem={toggleSelectItem}
                sortBy={sortBy}
                onUpdateFilters={setFilters}
                onResetFilters={handleResetAll}
                searchTerm={searchTerm}
                onSearchChange={handleSearchChange}
                onSortChange={handleSortChange}
                onItemsSoldRef={onItemsSoldRef}
            />
          </div>

          <div className="hidden lg:block">
            <ToBeSoldItems
                steamAccountId={steamAccountId!}
                items={selectedItems}
                onRemoveItem={removeSelectedItem}
                onClear={clearSelection}
                onQuantityChange={updateQuantity}
                onPriceChange={updatePrice}
                onSuccess={handleRemoveSoldItemsFromState}
            />
          </div>
        </div>
      </Box>
  );
};

export default Sell;